<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { PRICE_ESTIMATE_SOURCE_FALLBACK } from '../utils/imovelShowConstants';
import { formatBRL } from '../utils/imovelShowFormatters';

defineProps({
  estimate: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  applying: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  hasApplicable: {
    type: Boolean,
    default: false,
  },
  estimatedValue: {
    type: Number,
    default: null,
  },
  rangeLabel: {
    type: String,
    default: '',
  },
  sourceLabel: {
    type: String,
    default: '',
  },
  confidenceLabel: {
    type: String,
    default: '',
  },
  samples: {
    type: Number,
    default: 0,
  },
  dateLabel: {
    type: String,
    default: '',
  },
  applicationHistory: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['request', 'apply']);

const priceApplyReason = defineModel('priceApplyReason', {
  type: String,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Estimativa QPreço" density="cozy">
    <p class="text-[13px] leading-relaxed text-lais-text-body">
      Estime o preço com comparáveis da conta e aplique com motivo, origem,
      diferença e histórico auditável.
    </p>
    <LaisPrimaryButton
      class="mt-4 w-full"
      variant="primary"
      size="sm"
      icon="i-lucide-calculator"
      :is-loading="loading"
      :disabled="loading || applying"
      @click="emit('request')"
    >
      Estimar preço
    </LaisPrimaryButton>

    <div
      v-if="loading"
      class="mt-4 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] text-lais-text-body"
      aria-live="polite"
    >
      Calculando estimativa com imóveis comparáveis...
    </div>
    <div
      v-else-if="errorMessage"
      class="mt-4 rounded-lais-sm border border-red-200 bg-red-50 px-3 py-3 text-[13px] leading-relaxed text-red-700"
    >
      {{ errorMessage }}
    </div>
    <div
      v-else-if="estimate && !hasApplicable"
      class="mt-4 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] leading-relaxed text-lais-text-body"
    >
      {{ emptyMessage }}
    </div>

    <div v-if="estimate && hasApplicable" class="mt-4 grid gap-4">
      <div
        class="grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3 text-[13px]"
      >
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Valor estimado</span>
          <span class="font-semibold text-lais-text">{{
            formatBRL(estimatedValue)
          }}</span>
        </p>
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Faixa</span>
          <span class="text-right font-semibold text-lais-text">{{
            rangeLabel
          }}</span>
        </p>
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Fonte</span>
          <span class="font-semibold text-lais-text">{{ sourceLabel }}</span>
        </p>
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Confiança</span>
          <span class="font-semibold text-lais-text">{{
            confidenceLabel
          }}</span>
        </p>
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Amostra</span>
          <span class="font-semibold text-lais-text">
            {{ samples }} comparáveis
          </span>
        </p>
        <p class="flex items-center justify-between gap-3">
          <span class="text-lais-text-muted">Data</span>
          <span class="font-semibold text-lais-text">{{ dateLabel }}</span>
        </p>
      </div>

      <div class="grid gap-1.5">
        <label
          for="price-estimate-apply-reason"
          class="text-[12px] font-semibold text-lais-text"
        >
          Motivo para aplicar
        </label>
        <textarea
          id="price-estimate-apply-reason"
          v-model="priceApplyReason"
          rows="2"
          class="w-full rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[13px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
          placeholder="Ex: revisão aprovada pelo proprietário após análise QPreço"
        />
      </div>

      <LaisPrimaryButton
        class="w-full"
        variant="primary"
        size="sm"
        icon="i-lucide-check-circle-2"
        :is-loading="applying"
        :disabled="applying"
        @click="emit('apply')"
      >
        Aplicar estimativa
      </LaisPrimaryButton>
    </div>

    <div
      v-if="applicationHistory.length"
      class="mt-4 border-t border-lais-border-soft pt-3"
    >
      <p class="text-[12px] font-semibold text-lais-text">
        Histórico de aplicações
      </p>
      <ul class="mt-2 grid gap-2 text-[12px] text-lais-text-body">
        <li
          v-for="entry in applicationHistory.slice(0, 3)"
          :key="entry.confirmed_at || entry.confirmedAt"
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
        >
          {{ formatBRL(entry.applied_price || entry.appliedPrice) }}
          via {{ entry.source || PRICE_ESTIMATE_SOURCE_FALLBACK }}
          <span v-if="entry.reason">- {{ entry.reason }}</span>
        </li>
      </ul>
    </div>
  </LaisCard>
</template>
